import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { useHistory } from 'react-router'
import { generatePath } from 'react-router-dom'
import { Wrap } from 'shared/components/Base/Base'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { ButtonVerticalGroup } from 'shared/components/Button/ButtonVerticalGroup'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { LocationDropdown } from 'shared/components/Dropdown/LocationDropdown'
import { MonthYearInput } from 'shared/components/MaskedInput/MonthYearInput'
import {
  EmploymentHistoryContainer,
  EmploymentHistoryRowContent,
  EmploymentHistoryRowDoubleField,
} from 'shared/components/Onboarding/EmploymentHistory/EmploymentHistory.styles'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { useEmploymentHistory } from 'shared/hooks/query/useEmploymentHistory'
import { useScrollToErrors } from 'shared/hooks/useScrollToErrors'
import { primaryBlack } from 'shared/theme/colors'

import { ErrorMessage, ErrorMessageList } from '../../../../components/Message/ErrorMessage'
import { getEmploymentHistoryItemCache } from '../../../../hooks/query/useEmploymentHistoryItem'
import {
  createEmploymentHistoryItem,
  deleteEmploymentHistoryItem,
  EmploymentJob,
  EmploymentJobType,
  LeavingReason,
  updateEmploymentHistoryItem,
} from '../../../../models/employment'
import { User } from '../../../../models/user'
import { useUserProvider } from '../../../Context/UserContext'
import { EMPLOYMENT_ROUTES } from '../EmploymentHistory.constants'
import {
  getEmploymentHistoryPositionFormSchema,
  getEmploymentHistoryPositionInitialErrors,
  getEmploymentHistoryPositionInitialForm,
  leavingReasonOptions,
  PositionForm,
  trailerOptions,
  vehicleOptions,
} from './EmploymentHistoryPositionForm.constants'

interface EmploymentHistoryPositionFormProps {
  employmentHistoryItem?: EmploymentJob
  user: User
}

export function EmploymentHistoryPositionForm({
  user,
  employmentHistoryItem,
}: EmploymentHistoryPositionFormProps) {
  const history = useHistory()
  const isEditing = Boolean(employmentHistoryItem)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const {
    control,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<PositionForm>({
    mode: 'onSubmit',
    defaultValues: getEmploymentHistoryPositionInitialForm(employmentHistoryItem),
    resolver: yupResolver(getEmploymentHistoryPositionFormSchema()),
  })
  const isCurrentlyWorkingHere = watch('isCurrentlyWorkingHere')
  const jobType = watch('jobType')
  const leavingReason = watch('leavingReason')
  const isDrivingJob = jobType === EmploymentJobType.DRIVING
  const { refetch: refetchEmploymentHistory } = useEmploymentHistory({ userId: user.id })
  const queryClient = useQueryClient()
  const { showBanner } = useUserProvider()

  useScrollToErrors(errors)

  const onSubmit = async (formData: PositionForm) => {
    if (!formData.jobType) {
      return clearErrors()
    }

    setIsSubmitting(true)

    const payload = {
      job_type: formData.jobType,
      details: {
        start_date: formData.startDate,
        end_date: formData.isCurrentlyWorkingHere ? null : formData.endDate,
        company_address: formData.companyAddress,
        leaving_reason: formData.isCurrentlyWorkingHere ? null : formData.leavingReason,
        leaving_reason_detail: formData.isCurrentlyWorkingHere ? null : formData.leavingReasonDetail,
        company_name: formData.companyName,
        equipment_type: formData.equipmentType,
        trailer_type: formData.trailerType,
        position_held: formData.positionHeld,
      },
    }

    try {
      const item = isEditing
        ? await updateEmploymentHistoryItem(user.id, employmentHistoryItem?.id!, payload)
        : await createEmploymentHistoryItem(user.id, payload)

      await refetchEmploymentHistory()
      queryClient.removeQueries({
        queryKey: getEmploymentHistoryItemCache(employmentHistoryItem?.id!, user.id),
        exact: true,
      })

      if (!item?.details.isInPast3Years || item.jobType === EmploymentJobType.NOT_DRIVING)
        return history.push(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY)

      if (isEditing)
        return history.push(
          generatePath(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY_EDIT_FMCSR, { id: item.id })
        )

      return history.push(
        generatePath(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY_ADD_FMCSR, { id: item.id })
      )
    } catch (err: any) {
      showBanner('error', err)
    }

    setIsSubmitting(false)
  }

  async function handleDeleteItem() {
    if (isEditing) {
      setIsDeleting(true)
      try {
        await deleteEmploymentHistoryItem(user.id, employmentHistoryItem?.id!)
        await refetchEmploymentHistory()
        history.push(EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY)
      } catch (err: any) {
        showBanner('error', err)
      }
      setIsDeleting(false)
    }
  }

  const closeModal = () => {
    setShowDeleteModal(false)
  }

  function maybeRenderInitialErrors() {
    if (!isEditing || !employmentHistoryItem) return null

    const initialErrors = getEmploymentHistoryPositionInitialErrors(employmentHistoryItem)

    if (initialErrors.length === 0) return null

    return (
      <>
        <ErrorMessage>
          <Text variant="header4">Please complete the missing information:</Text>
          <ErrorMessageList>
            {initialErrors.map((error) => (
              <li key={error}>
                <Text variant="paragraph2">{error}</Text>
              </li>
            ))}
          </ErrorMessageList>
        </ErrorMessage>
        <Box m={3} />
      </>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <EmploymentHistoryContainer>
          <Text variant="header2" align="center">
            {isEditing ? 'Edit' : 'Add a'} position
          </Text>
          <Box mt={2} />

          <EmploymentHistoryRowContent $vertical>{maybeRenderInitialErrors()}</EmploymentHistoryRowContent>

          <EmploymentHistoryRowContent>
            <Controller
              render={({ field, fieldState }) => (
                <RadioButtonGroup
                  {...field}
                  options={[
                    {
                      label: 'Class 8 tractor/trailer driving job',
                      value: EmploymentJobType.DRIVING,
                    },
                    {
                      label: 'Non-Class 8 tractor/trailer driving job',
                      value: EmploymentJobType.NOT_DRIVING,
                    },
                  ]}
                  width="100%"
                  row
                  noMargin
                  error={fieldState.error?.message}
                />
              )}
              name="jobType"
              control={control}
            />
          </EmploymentHistoryRowContent>

          <Box mt={3} />

          {jobType && (
            <>
              <Controller
                render={({ field, fieldState }) => (
                  <TextInput {...field} label="Company" error={fieldState.error?.message} />
                )}
                name="companyName"
                control={control}
              />

              {!isDrivingJob && (
                <>
                  <Box mt={1} />
                  <Controller
                    render={({ field, fieldState }) => (
                      <TextInput {...field} label="Job Title" error={fieldState.error?.message} noMargin />
                    )}
                    name="positionHeld"
                    control={control}
                  />
                </>
              )}

              {/*TODO: @taylangocmen add a reference forwarding in LocationInput */}
              {isDrivingJob && (
                <>
                  <Box mt={1} />
                  <EmploymentHistoryRowContent>
                    <Controller
                      render={({ field, fieldState }) => (
                        <LocationDropdown
                          {...field}
                          inputProps={{
                            label: 'City, state',
                          }}
                          error={fieldState.error?.message}
                        />
                      )}
                      name="companyAddress"
                      control={control}
                    />
                  </EmploymentHistoryRowContent>
                </>
              )}

              <Box mt={3} />
              <EmploymentHistoryRowContent>
                <Controller
                  render={({ field }) => <Checkbox {...field} label="Current position" />}
                  name="isCurrentlyWorkingHere"
                  control={control}
                />
              </EmploymentHistoryRowContent>

              <EmploymentHistoryRowDoubleField>
                <Controller
                  render={({ field, fieldState }) => (
                    <MonthYearInput {...field} label="Start date" error={fieldState.error?.message} />
                  )}
                  name="startDate"
                  control={control}
                />

                {!isCurrentlyWorkingHere && (
                  <Controller
                    render={({ field, fieldState }) => (
                      <MonthYearInput {...field} label="End date" error={fieldState.error?.message} />
                    )}
                    name="endDate"
                    control={control}
                  />
                )}
              </EmploymentHistoryRowDoubleField>

              {isDrivingJob && (
                <>
                  <Controller
                    render={({ field, fieldState }) => (
                      <Dropdown
                        {...field}
                        error={fieldState.error?.message}
                        options={vehicleOptions}
                        label="Vehicle"
                        staticLabel="Vehicle"
                        maxHeight={500}
                        isFullWidth
                      />
                    )}
                    name="equipmentType"
                    control={control}
                  />
                </>
              )}

              {isDrivingJob && (
                <>
                  <Box mt={1} />
                  <Controller
                    render={({ field, fieldState }) => (
                      <Dropdown
                        {...field}
                        error={fieldState.error?.message}
                        options={trailerOptions}
                        label="Trailer"
                        staticLabel="Trailer"
                        maxHeight={500}
                        isFullWidth
                      />
                    )}
                    name="trailerType"
                    control={control}
                  />
                </>
              )}

              {!isCurrentlyWorkingHere && (
                <>
                  <Box mt={isDrivingJob ? 1 : 0} />
                  <Controller
                    render={({ field, fieldState }) => (
                      <Dropdown
                        {...field}
                        error={fieldState.error?.message}
                        options={leavingReasonOptions}
                        label="Reason for leaving"
                        staticLabel="Reason for leaving"
                        maxHeight={500}
                        isFullWidth
                      />
                    )}
                    name="leavingReason"
                    control={control}
                  />
                </>
              )}

              {isDrivingJob && !isCurrentlyWorkingHere && leavingReason === LeavingReason.OTHER && (
                <Controller
                  render={({ field, fieldState }) => (
                    <TextInput
                      {...field}
                      placeholder="Add a short explanation"
                      error={fieldState.error?.message}
                    />
                  )}
                  name="leavingReasonDetail"
                  control={control}
                />
              )}
            </>
          )}

          <Box m={4} />

          <Button type="submit" variant="primary" size="extraLarge" isLoading={isSubmitting}>
            {isEditing ? 'Save' : 'Continue'}
          </Button>
          <Box mt={4} />
          <ButtonVerticalGroup
            leftButton={
              <Button variant="text" href={EMPLOYMENT_ROUTES.ONBOARDING_EMPLOYMENT_HISTORY}>
                Cancel
              </Button>
            }
            rightButton={
              isEditing ? (
                <Button variant="text" onClick={() => setShowDeleteModal(true)}>
                  Remove
                </Button>
              ) : null
            }
          />
        </EmploymentHistoryContainer>
      </form>

      <SimpleDialog isOpen={showDeleteModal} onClose={closeModal} maxWidth="472px">
        <Wrap width="700px" maxWidth="100%">
          <Text variant="header2" fontColor={primaryBlack} mb={2}>
            Remove position?
          </Text>
          <Text variant="paragraph1">Your entry will be lost.</Text>
          <Box m={5} />
          <Button variant="primary" onClick={handleDeleteItem} margin="0 auto" isLoading={isDeleting}>
            Remove Position
          </Button>
          <Box m={3} />
          <Button variant="text" onClick={closeModal} margin="0 auto">
            Cancel
          </Button>
        </Wrap>
      </SimpleDialog>
    </>
  )
}
